import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, ChevronDown, ChevronUp, AlertCircle, Sparkles, ShieldCheck, Calendar } from 'lucide-react';

const PACKAGES = {
  basic: {
    name: 'Basic Shine',
    tagline: 'Essential maintenance for a clean ride',
    prices: { small: 149, medium: 159, large: 169, xlarge: 189 },
    duration: '~1.5 hours',
    includes: [
      'Exterior Hand Wash & Dry (Two-Bucket Method)',
      'Wheels & Tyres Cleaned and Dressed',
      'Full Interior Vacuum (Seats, Floors, Boot)',
      'Interior Surfaces Wiped Down',
      'Windows Cleaned Inside and Out',
    ],
    color: '#00BFA5',
    badge: null,
  },
  deluxe: {
    name: 'Deluxe Shine',
    tagline: 'Deep clean and surface protection',
    prices: { small: 210, medium: 230, large: 240, xlarge: 250 },
    duration: '~2.5 - 3 hours',
    includes: [
      'Everything in Basic Shine, PLUS:',
      'Dashboard Deep Clean & Protectant',
      'Interior Plastics & Trim Dressed',
      'Door Jambs Thoroughly Cleaned',
      'Deodorising Treatment',
      'Detailed Wheel & Arch Clean',
    ],
    color: '#6B46C0',
    badge: 'Best Value',
  },
} as const;

const VEHICLE_SIZES = [
  { id: 'small', name: 'Small', note: 'Yaris, Swift, Polo' },
  { id: 'medium', name: 'Medium', note: 'Corolla, i30, Civic' },
  { id: 'large', name: 'Large SUV / Ute', note: 'RAV4, Hilux, Ranger' },
  { id: 'xlarge', name: 'X-Large / 4WD', note: 'Land Cruiser, Patrol, Carnival' },
] as const;

const EXTRAS = [
  { id: 'petHair', name: 'Pet Hair Removal', price: 50, icon: '🐕' },
  { id: 'engineBay', name: 'Engine Bay Detail', price: 50, icon: '🔧' },
  { id: 'headlights', name: 'Headlight Restore', price: 80, icon: '💡' },
  { id: 'mould', name: 'Mould/Biohazard', price: 100, icon: '⚠️' },
] as const;

type PackageTier = keyof typeof PACKAGES;
type VehicleSizeId = typeof VEHICLE_SIZES[number]['id'];

export default function BookingForm() {
  const [selectedPackage, setSelectedPackage] = useState<PackageTier | null>(null);
  const [vehicleSize, setVehicleSize] = useState<VehicleSizeId>('medium');
  const [expandedPackage, setExpandedPackage] = useState<PackageTier | null>(null);
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);
  const [formValues, setFormValues] = useState({ name: '', phone: '', email: '', date: '', address: '', notes: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'succeeded' | 'error'>('idle');

  const toggleExtra = (id: string) => {
    setSelectedExtras(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const calculateTotal = () => {
    if (!selectedPackage) return 0;
    const basePrice = PACKAGES[selectedPackage].prices[vehicleSize];
    const extrasPrice = selectedExtras.reduce((sum, id) => {
      const extra = EXTRAS.find(e => e.id === id);
      return sum + (extra?.price || 0);
    }, 0);
    return basePrice + extrasPrice;
  };

  const generateICS = () => {
    const title = `${formValues.name} | REGO | ${selectedPackage ? PACKAGES[selectedPackage].name : 'Detail'}`;
    const description = `Phone: ${formValues.phone}\\nExtras: ${selectedExtras.join(', ')}\\nNotes: ${formValues.notes}`;
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'BEGIN:VEVENT',
      `DTSTART:${formValues.date.replace(/-/g, '')}T090000Z`,
      `DTEND:${formValues.date.replace(/-/g, '')}T120000Z`,
      `SUMMARY:${title}`,
      `DESCRIPTION:${description}`,
      `LOCATION:${formValues.address}`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', `${formValues.name}-booking.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    
    try {
      const response = await fetch('/api/send-booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formValues,
          packageTier: selectedPackage,
          size: vehicleSize,
          total: calculateTotal(),
        }),
      });

      if (response.ok) {
        setStatus('succeeded');
      } else {
        setStatus('error');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setStatus('error');
    }
  };

  if (status === 'succeeded') {
    return (
      <div className="max-w-xl mx-auto text-center py-12 px-6 animate-in fade-in zoom-in duration-300">
        <div className="w-20 h-20 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
          <Check className="w-10 h-10 text-white" />
        </div>
        <h2 className="text-3xl font-black text-slate-900 mb-4 tracking-tight uppercase italic">Booking Received</h2>
        <div className="bg-purple-50 p-6 rounded-[2rem] border-2 border-purple-100 mb-8">
            <p className="text-[10px] font-black uppercase text-purple-600 mb-3 tracking-widest text-center">Save to Schedule</p>
            <Button onClick={generateICS} className="w-full bg-purple-600 hover:bg-purple-700 text-white font-black uppercase rounded-xl py-6 shadow-lg">
                <Calendar className="mr-2 w-5 h-5" /> Download Calendar Invite
            </Button>
        </div>
        <Button onClick={() => window.location.reload()} variant="outline" className="border-2 rounded-xl font-black uppercase text-[10px] tracking-widest">New Booking</Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto pb-20 px-4">
      <div className="mb-10 text-center">
        <h2 className="text-4xl font-black text-slate-900 mb-2 tracking-tighter uppercase italic">Book Your Shine</h2>
      </div>

      <form onSubmit={handleBookingSubmit} className="space-y-10">
        <div className="space-y-4">
          <label className="text-[10px] font-black uppercase tracking-widest text-purple-600 block ml-1">1. Select Package</label>
          <div className="grid gap-4">
            {(Object.entries(PACKAGES) as [PackageTier, typeof PACKAGES[PackageTier]][]).map(([key, pkg]) => (
              <div 
                key={key}
                onClick={() => setSelectedPackage(key)}
                className={`relative p-6 rounded-3xl border-2 cursor-pointer transition-all ${
                  selectedPackage === key ? 'border-purple-600 bg-purple-50/30 ring-4 ring-purple-100' : 'border-slate-100 bg-white hover:border-slate-200 shadow-sm'
                }`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight italic">{pkg.name}</h3>
                    <p className="text-xs text-slate-500 font-bold uppercase tracking-tight">{pkg.tagline}</p>
                  </div>
                  <div className="text-right text-xl font-black text-purple-600 tracking-tighter">from ${pkg.prices.small}</div>
                </div>
                
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm" 
                  className="h-auto p-0 text-purple-600 font-black uppercase text-[9px] tracking-widest flex items-center gap-1"
                  onClick={(e) => { e.stopPropagation(); setExpandedPackage(expandedPackage === key ? null : key); }}
                >
                  {expandedPackage === key ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  {expandedPackage === key ? 'Hide inclusions' : 'View inclusions'}
                </Button>

                {expandedPackage === key && (
                  <ul className="mt-4 space-y-2 border-t pt-4">
                    {pkg.includes.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-slate-600 font-bold uppercase tracking-tight">
                        <Check className="w-3 h-3 text-teal-500 mt-0.5 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>

        {selectedPackage && (
          <div className="space-y-4">
            <label className="text-[10px] font-black uppercase tracking-widest text-purple-600 block ml-1">2. Vehicle Size</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {VEHICLE_SIZES.map((size) => (
                <button
                  key={size.id}
                  type="button"
                  onClick={() => setVehicleSize(size.id)}
                  className={`p-4 rounded-2xl border-2 text-left transition-all ${
                    vehicleSize === size.id ? 'border-teal-500 bg-teal-50/50 ring-4 ring-teal-100' : 'border-slate-100 bg-white hover:border-slate-200'
                  }`}
                >
                  <div className="text-[10px] font-black uppercase text-slate-900 tracking-tighter">{size.name}</div>
                  <div className="font-black text-slate-900 text-lg tracking-tighter">${PACKAGES[selectedPackage].prices[size.id]}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <label className="text-[10px] font-black uppercase tracking-widest text-purple-600 block flex items-center gap-2 ml-1">
            <Sparkles className="w-3 h-3" /> 3. Optional Add-Ons
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {EXTRAS.map((extra) => (
              <div 
                key={extra.id}
                onClick={() => toggleExtra(extra.id)}
                className={`flex items-center justify-between p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                  selectedExtras.includes(extra.id) ? 'border-amber-400 bg-amber-50 shadow-md ring-2 ring-amber-100' : 'border-slate-100 bg-white hover:border-slate-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{extra.icon}</span>
                  <div>
                    <p className="text-[10px] font-black text-slate-900 uppercase tracking-tighter">{extra.name}</p>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">+${extra.price}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedPackage && (
          <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white flex justify-between items-center shadow-2xl relative overflow-hidden border-b-8 border-slate-800">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.3em] text-purple-400 mb-1">Total Estimate</p>
              <div className="text-5xl font-black text-white tracking-tighter">${calculateTotal()}</div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <label className="text-[10px] font-black uppercase tracking-widest text-purple-600 block ml-1">4. Final Details</label>
          <div className="grid md:grid-cols-2 gap-4">
            <input className="flex h-14 w-full rounded-2xl border-2 border-slate-100 bg-white px-4 text-sm font-black uppercase placeholder:text-slate-400 focus:outline-none focus:border-purple-600 shadow-sm" required placeholder="Full Name" onChange={e => setFormValues({...formValues, name: e.target.value})} />
            <input className="flex h-14 w-full rounded-2xl border-2 border-slate-100 bg-white px-4 text-sm font-black uppercase placeholder:text-slate-400 focus:outline-none focus:border-purple-600 shadow-sm" type="tel" required placeholder="Phone Number" onChange={e => setFormValues({...formValues, phone: e.target.value})} />
            <input className="flex h-14 w-full rounded-2xl border-2 border-slate-100 bg-white px-4 text-sm font-black placeholder:text-slate-400 focus:outline-none focus:border-purple-600 shadow-sm" type="email" required placeholder="EMAIL@ADDRESS.COM" onChange={e => setFormValues({...formValues, email: e.target.value})} />
            <input className="flex h-14 w-full rounded-2xl border-2 border-slate-100 bg-white px-4 text-sm font-black uppercase placeholder:text-slate-400 focus:outline-none focus:border-purple-600 shadow-sm" type="date" required onChange={e => setFormValues({...formValues, date: e.target.value})} />
            <input className="md:col-span-2 flex h-14 w-full rounded-2xl border-2 border-slate-100 bg-white px-4 text-sm font-black uppercase placeholder:text-slate-400 focus:outline-none focus:border-purple-600 shadow-sm" required placeholder="Service Address (Street & Suburb)" onChange={e => setFormValues({...formValues, address: e.target.value})} />
          </div>
        </div>

        <div className="space-y-6 pt-6 border-t-2 border-slate-100">
          <div className="bg-teal-50 p-6 rounded-3xl border-2 border-teal-100 flex gap-4 shadow-sm">
            <ShieldCheck className="w-5 h-5 text-teal-600 shrink-0" />
            <p className="text-[10px] font-black text-teal-800 leading-relaxed uppercase">
              By submitting, you agree to our Terms of Trade. Payment is required upon completion. No Bullshit.
            </p>
          </div>

          <button
            type="submit"
            disabled={status === 'submitting' || !selectedPackage}
            className="w-full bg-[#00BFA5] hover:bg-[#00a892] text-white h-20 rounded-3xl font-black uppercase tracking-[0.2em] shadow-xl hover:scale-[1.02] disabled:opacity-50 italic text-lg"
          >
            {status === 'submitting' ? 'Sending Request...' : 'Send Booking Request'}
          </button>
        </div>

        {status === 'error' && (
          <div className="flex items-center justify-center gap-2 text-red-500 text-[10px] font-black uppercase bg-red-50 p-4 rounded-xl">
            <AlertCircle className="w-4 h-4" /> Error sending request. Try again or call us.
          </div>
        )}
      </form>
    </div>
  );
}